import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { Camera, Send, X, Clock, AlertCircle } from 'lucide-react';
import * as api from '../../services/api';
import { supabase } from '../../services/supabase';
import { FlashAlertInstance } from '../../types';
import { Button } from '../ui/Button';

interface FlashAlertPopupProps {
    clientId: string;
}

export const FlashAlertPopup: React.FC<FlashAlertPopupProps> = ({ clientId }) => {
    const [activeAlerts, setActiveAlerts] = useState<FlashAlertInstance[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedPhoto, setSelectedPhoto] = useState<File | null>(null);
    const [photoPreview, setPhotoPreview] = useState<string | null>(null);
    const [comment, setComment] = useState('');
    const [timeRemaining, setTimeRemaining] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const lastNotifiedId = useRef<string | null>(null);
    const dismissedIds = useRef<Set<string>>(new Set());

    const fetchAlerts = async () => {
        if (!clientId) return;
        try {
            console.log(`[FlashAlert] Checking active alerts for client ${clientId}...`);
            
            const { data: alertsData, error: alertsError } = await supabase
                .from('control_alerts')
                .select('*')
                .eq('client_id', clientId)
                .eq('status', 'PENDING')
                .order('triggered_at', { ascending: true });

            if (alertsError) {
                console.error("[FlashAlert] Fetch error:", alertsError);
                return;
            }

            const configsMap: Record<string, unknown> = {};
            if (alertsData && alertsData.length > 0) {
                const configIds = Array.from(new Set(alertsData.map(a => a.config_id).filter(Boolean)));
                if (configIds.length > 0) {
                    const { data: configsData, error: configsError } = await supabase
                        .from('control_alert_configs')
                        .select('*')
                        .in('id', configIds);
                    if (!configsError && configsData) {
                        configsData.forEach(c => {
                            configsMap[c.id] = c;
                        });
                    } else if (configsError) {
                        console.warn("[FlashAlert] Config fetch warning:", configsError);
                    }
                }
            }
            
            const now = new Date();
            const threshold = new Date(now.getTime() + 60 * 1000); 

            const triggered = (alertsData || []).filter((a) => {
                const triggeredTime = new Date(a.triggered_at);
                // Only show if triggered time is now or very soon, and not dismissed locally
                return triggeredTime <= threshold && !dismissedIds.current.has(a.id);
            }).map((a) => {
                const configData = a.config_id ? configsMap[a.config_id] : null;
                return {
                    ...api.mapFlashAlertInstanceFromDB(a),
                    isNotified: a.is_notified as boolean,
                    config: configData ? api.mapFlashAlertConfigFromDB(configData) : undefined
                };
            });
            
            if (triggered.length > 0) {
                console.log(`[FlashAlert] Found ${triggered.length} active alerts.`);
            }

            // Mark as notified without any audio sound
            for (const alert of triggered) {
                if (!alert.isNotified && lastNotifiedId.current !== alert.id) {
                    lastNotifiedId.current = alert.id;
                    try {
                        await supabase.from('control_alerts').update({ is_notified: true }).eq('id', alert.id);
                    } catch (e) {
                        console.error("Failed to update alert is_notified", e);
                    }
                }
            }

            setActiveAlerts(triggered);
        } catch (error) {
            console.error("Critical error in fetchAlerts", error);
        }
    };

    useEffect(() => {
        if (!clientId) {
            console.warn("[FlashAlert] Rendered without clientId");
            return;
        }

        console.log(`[FlashAlert] Component mounted for ${clientId}`);
        fetchAlerts();

        const channel = supabase
            .channel(`public:control_alerts:client:${clientId}`)
            .on('postgres_changes', { 
                event: '*', 
                schema: 'public', 
                table: 'control_alerts', 
                filter: `client_id=eq.${clientId}` 
            }, (payload) => {
                console.log('[FlashAlert] REALTIME:', payload);
                // When an alert is deleted or status changed, we fetch again
                fetchAlerts();
            })
            .subscribe();

        const pollInterval = setInterval(fetchAlerts, 10000);
        
        return () => {
            console.log(`[FlashAlert] Component unmounting for ${clientId}`);
            supabase.removeChannel(channel);
            clearInterval(pollInterval);
        };
    }, [clientId]);

    const currentAlert = activeAlerts[0];

    useEffect(() => {
        if (!currentAlert?.deadlineAt) {
            setTimeRemaining(null);
            return;
        }

        const updateTimer = () => {
            const now = new Date();
            const deadline = new Date(currentAlert.deadlineAt!);
            if (isNaN(deadline.getTime())) {
                setTimeRemaining(null);
                return;
            }
            const diff = deadline.getTime() - now.getTime();
            if (diff <= 0) {
                setTimeRemaining("ZEIT ABGELAUFEN!");
                return;
            }
            const mins = Math.floor(diff / 60000);
            const secs = Math.floor((diff % 60000) / 1000);
            setTimeRemaining(`${mins}:${secs.toString().padStart(2, '0')}`);
        };

        updateTimer();
        const timer = setInterval(updateTimer, 1000);
        return () => clearInterval(timer);
    }, [currentAlert?.id, currentAlert?.deadlineAt]);

    const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files?.[0]) {
            const file = e.target.files[0];
            setSelectedPhoto(file);
            const reader = new FileReader();
            reader.onloadend = () => setPhotoPreview(reader.result as string);
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async () => {
        if (!currentAlert) return;
        const alertId = currentAlert.id;
        setIsSubmitting(true);
        try {
            await api.submitFlashAlert(alertId, selectedPhoto || undefined, comment);
            console.log(`[FlashAlert] Submission success for ${alertId}`);
            
            // Mark as dismissed locally to prevent it popping back up
            dismissedIds.current.add(alertId);
            
            // Immediately update state to trigger animation
            setActiveAlerts(prev => prev.filter(a => a.id !== alertId));
            
            // Reset form
            setSelectedPhoto(null);
            setPhotoPreview(null);
            setErrorMessage(null);
        } catch (error) {
            console.error("Submission failed", error);
            setErrorMessage("Fehler bei Übertragung. Bitte versuche es erneut.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const portalElement = document.body;
    if (!portalElement) return null;

    return createPortal(
        <AnimatePresence mode="wait">
            {currentAlert && (
                <div 
                    key={currentAlert.id} 
                    className="fixed inset-0 z-[9999999] flex items-center justify-center p-4 bg-black/95 backdrop-blur-2xl"
                >
                    <motion.div 
                        initial={{ scale: 0.8, opacity: 0, y: 50 }}
                        animate={{ scale: 1, opacity: 1, y: 0 }}
                        exit={{ scale: 1.1, opacity: 0 }}
                        transition={{ type: "spring", damping: 25, stiffness: 300 }}
                        className="w-full max-w-lg bg-surface border-2 border-accent rounded-[2.5rem] overflow-hidden shadow-[0_0_100px_rgba(var(--color-accent),0.4)]"
                    >
                        <div className="bg-accent/10 p-6 flex items-center justify-between border-b border-white/5">
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 rounded-full bg-accent flex items-center justify-center animate-pulse">
                                    <AlertCircle className="text-white w-7 h-7" />
                                </div>
                                <div>
                                    <h3 className="text-2xl font-black uppercase tracking-wider text-accent">BLITZ-KONTROLLE!</h3>
                                    <p className="text-[10px] text-text-secondary font-mono tracking-widest uppercase">Gehorsam ohne Zögern</p>
                                </div>
                            </div>
                            {timeRemaining && (
                                <div className="bg-red-500/20 px-3 py-1 rounded-full border border-red-500/50 flex items-center gap-2">
                                    <Clock className="w-4 h-4 text-red-500" />
                                    <span className="text-sm font-black text-red-500 font-mono">{timeRemaining}</span>
                                </div>
                            )}
                        </div>

                        <div className="p-8 space-y-6">
                            <div className="space-y-2">
                                <h4 className="text-2xl font-black text-text-main leading-tight">
                                    {currentAlert.config?.title || "Blitz-Kontrolle"}
                                </h4>
                                <p className="text-text-secondary italic text-lg">
                                    {currentAlert.config?.description || "Folge den Anweisungen deiner Göttin."}
                                </p>
                            </div>

                            <div className="bg-white/5 rounded-2xl p-5 border border-white/5 text-lg">
                                <span className="text-accent font-bold italic">Beweisfoto einreichen!</span>
                            </div>

                            <div className="space-y-4">
                                {photoPreview ? (
                                    <div className="relative aspect-video rounded-3xl overflow-hidden border-2 border-accent">
                                        <img src={photoPreview} className="w-full h-full object-cover" alt="Preview" />
                                        <button 
                                            onClick={() => { setSelectedPhoto(null); setPhotoPreview(null); }}
                                            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/60 flex items-center justify-center hover:bg-red-500 transition-colors"
                                        >
                                            <X className="w-5 h-5" />
                                        </button>
                                    </div>
                                ) : (
                                    <button 
                                        onClick={() => fileInputRef.current?.click()}
                                        className="w-full aspect-video rounded-3xl border-2 border-dashed border-white/10 bg-white/5 hover:bg-white/10 hover:border-accent/30 transition-all flex flex-col items-center justify-center gap-4 group"
                                    >
                                        <div className="w-20 h-20 rounded-full bg-white/5 flex items-center justify-center group-hover:scale-110 transition-all">
                                            <Camera className="w-10 h-10 text-accent" />
                                        </div>
                                        <span className="text-sm font-black uppercase tracking-widest opacity-50">Foto wählen / Aufnehmen</span>
                                    </button>
                                )}
                                <input type="file" ref={fileInputRef} className="hidden" accept="image/*" capture="environment" onChange={handlePhotoChange} />
                            </div>

                            <textarea 
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                placeholder="Deine Nachricht..."
                                className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 focus:border-accent/50 outline-none placeholder:opacity-10 text-lg"
                                rows={2}
                            />

                            {errorMessage && (
                                <div className="p-3 bg-red-500/20 border border-red-500/40 rounded-xl text-red-300 text-xs text-center font-bold">
                                    {errorMessage}
                                </div>
                            )}

                            <Button 
                                fullWidth 
                                size="lg"
                                disabled={!selectedPhoto || isSubmitting}
                                loading={isSubmitting}
                                onClick={handleSubmit}
                                className="h-18 text-xl rounded-2xl shadow-[0_15px_30px_rgba(var(--color-accent),0.4)]"
                            >
                                <Send className="w-6 h-6 mr-2" />
                                EINREICHEN
                            </Button>
                        </div>

                        <div className="bg-black/40 p-5 text-center">
                            <p className="text-[10px] text-accent/40 uppercase tracking-[0.4em] font-black italic">Widerstand ist zwecklos • Fairyrealm Control</p>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>,
        portalElement
    );
};
